/**
 * *Pridať fakt*, *Už neplatí od*, *Chybné* and their undo (PRD-0075 R-C3, R-C4), in the signed-in
 * analyst's name. A mark answers the node or edge as it left it: the canvas snapshot takes the new
 * properties at once (so what no longer holds hides there too), and the reads that show it — the
 * entity detail, its neighbours, the history and its header — are read again.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { entityDetailsQueryKey } from '@/components/neo4j/shared/useEntityDetails'
import { entityHistoryKeys } from '@/features/entity-history/useEntityHistory'
import { caseKeys } from '@/hooks/use-case-queries'
import {
  type AssertFactRequest,
  analystFactsApi,
  type InvalidateFactRequest,
  type MarkedGraphItem,
  type RetireRequest,
} from '@/services/api/analyst-facts'
import { useInvestigationsStoreApiOptional } from '@/stores/investigations-store-provider'

/** One mark (or its undo); an entity by its stable uid, a relationship by its element id. */
export type FactMark =
  | { kind: 'assert'; uid: string; body: AssertFactRequest }
  | { kind: 'invalidateProperty'; uid: string; key: string; body: InvalidateFactRequest }
  | { kind: 'revalidateProperty'; uid: string; key: string }
  | { kind: 'invalidateRelationship'; id: string; body: InvalidateFactRequest }
  | { kind: 'revalidateRelationship'; id: string }
  | { kind: 'retireEntity'; uid: string; body: RetireRequest }
  | { kind: 'restoreEntity'; uid: string }
  | { kind: 'retireRelationship'; id: string; body: RetireRequest }
  | { kind: 'restoreRelationship'; id: string }

function send(caseId: string, mark: FactMark): Promise<MarkedGraphItem> {
  switch (mark.kind) {
    case 'assert':
      return analystFactsApi.assertFact(caseId, mark.uid, mark.body)
    case 'invalidateProperty':
      return analystFactsApi.invalidateProperty(caseId, mark.uid, mark.key, mark.body)
    case 'revalidateProperty':
      return analystFactsApi.revalidateProperty(caseId, mark.uid, mark.key)
    case 'invalidateRelationship':
      return analystFactsApi.invalidateRelationship(caseId, mark.id, mark.body)
    case 'revalidateRelationship':
      return analystFactsApi.revalidateRelationship(caseId, mark.id)
    case 'retireEntity':
      return analystFactsApi.retireEntity(caseId, mark.uid, mark.body)
    case 'restoreEntity':
      return analystFactsApi.restoreEntity(caseId, mark.uid)
    case 'retireRelationship':
      return analystFactsApi.retireRelationship(caseId, mark.id, mark.body)
    case 'restoreRelationship':
      return analystFactsApi.restoreRelationship(caseId, mark.id)
  }
}

export function useFactMark(caseId: string) {
  const queryClient = useQueryClient()
  const store = useInvestigationsStoreApiOptional()
  return useMutation({
    mutationFn: (mark: FactMark) => send(caseId, mark),
    onSuccess: marked => {
      store?.getState().patchElementProperties(marked.id, marked.properties)
      for (const queryKey of [
        entityDetailsQueryKey(caseId, marked.id),
        caseKeys.graphEntityDetail(caseId, marked.id),
        caseKeys.graphRelationshipDetail(caseId, marked.id),
        // Every neighbourhood: a relationship shows in both of its entities' lists.
        ['graph', caseId, 'neighbors'],
        // The histories and their headers (the summaries sit under the same key).
        entityHistoryKeys.all(caseId),
      ]) {
        void queryClient.invalidateQueries({ queryKey })
      }
    },
  })
}
